import { Directive, ElementRef, InputSignal, effect, input, DestroyRef, inject } from '@angular/core';

@Directive({
  selector: '[libTypewriterAnimation]',
  standalone: true,
})
export class TypewriterAnimationDirective {
  libTypewriterAnimation: InputSignal<string> = input.required<string>();
  speed: InputSignal<number> = input<number>(100);

  private destroyRef = inject(DestroyRef);
  private intervalId: ReturnType<typeof setInterval> | null = null;

  constructor(private el: ElementRef) {
    effect(() => {
      this.stopAnimation(); // Stop any ongoing animation
      this.animateText(this.libTypewriterAnimation(), this.speed());
    });

    this.destroyRef.onDestroy(() => this.stopAnimation());
  }

  private animateText(text: string, speed: number): void {
    const element = this.el.nativeElement;
    const parentElement = element.parentElement; // Get the parent element
    element.textContent = '';
    const tokens = this.createTokens(text);
    if (tokens.length < 3) {
      element.textContent = text;
      return;
    }
    element.textContent = tokens[0];
    let i = 1;

    this.intervalId = setInterval(() => {
      if (i < tokens.length) {
        element.textContent += tokens[i];
        i++;
        if (parentElement.scrollHeight > parentElement.offsetHeight) {
          parentElement.scrollTo({ top: parentElement.scrollHeight, behavior: 'smooth' });
        }
      } else {
        this.stopAnimation();
      }
    }, speed);
  }

  private createTokens(text: string): string[] {
    const tokens = (text.match(/\S+|\s+/g) || []);
    return tokens
      .reduce((acc: string[], current: string, index: number, array: string[]) => {
        if (index === 0) {
          acc.push(current);
        } else
        if (index % 2 !== 0) {
          const nextWord = array[index + 1] || '';
          acc.push(current + nextWord);
        }
        return acc;
      }, []);
  }

  private stopAnimation(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
